// --- Selection helpers (standalone functions for Lizard complexity analysis) ---

export type UnifiedItem = { type: "folder" | "document"; id: string };

export function applyRangeSelection(
  itemId: string,
  unifiedIndex: number,
  anchorIndex: number | null,
  unifiedItems: UnifiedItem[],
  isSelected: (item: UnifiedItem) => boolean,
  setSelected: (item: UnifiedItem, selected: boolean) => void
): number | null {
  const anchorItem = anchorIndex !== null ? unifiedItems[anchorIndex] : null;
  const anchorStillSelected = anchorItem && isSelected(anchorItem);

  if (anchorIndex !== null && anchorStillSelected) {
    const startIndex = Math.min(anchorIndex, unifiedIndex);
    const endIndex = Math.max(anchorIndex, unifiedIndex);
    unifiedItems.forEach((item, i) => {
      setSelected(item, i >= startIndex && i <= endIndex);
    });
    return anchorIndex;
  }

  unifiedItems.forEach((item) => {
    setSelected(item, item.id === itemId);
  });
  return unifiedIndex;
}

export function applyModifierSelection(
  unifiedIndex: number,
  unifiedItems: UnifiedItem[],
  isSelected: (item: UnifiedItem) => boolean,
  setSelected: (item: UnifiedItem, selected: boolean) => void
): void {
  const currentItem = unifiedItems[unifiedIndex];
  setSelected(currentItem, !isSelected(currentItem));
}

export interface RowClickDeps {
  showCheckbox: boolean;
  hasStore: boolean;
  anchorIndex: number | null;
  unifiedItems: UnifiedItem[];
  isItemSelected: (item: UnifiedItem) => boolean;
  setItemSelected: (item: UnifiedItem, selected: boolean) => void;
  setAnchorIndex: (index: number | null) => void;
}

export function createRowClickHandler(
  deps: RowClickDeps,
  itemId: string,
  unifiedIndex: number
): (event: React.MouseEvent<HTMLDivElement>) => void {
  return function handleRowClick(event: React.MouseEvent<HTMLDivElement>) {
    if (!rowSelectionEnabled(deps)) return;

    const target = rowClickTarget(event);
    if (!target) return;
    const { isCheckboxClick, isTitleClick } = target;

    // Shift-click: range selection. Attached via onClickCapture on the row so
    // stopPropagation here prevents Radix Checkbox's onClick (and onCheckedChange)
    // from also toggling the row — which would otherwise undo the range selection
    // for the clicked row (WHA-2995).
    if (event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      const newAnchor = applyRangeSelection(
        itemId,
        unifiedIndex,
        deps.anchorIndex,
        deps.unifiedItems,
        deps.isItemSelected,
        deps.setItemSelected
      );
      deps.setAnchorIndex(newAnchor);
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      event.stopPropagation();
      applyModifierSelection(
        unifiedIndex,
        deps.unifiedItems,
        deps.isItemSelected,
        deps.setItemSelected
      );
      deps.setAnchorIndex(unifiedIndex);
      return;
    }

    // Plain click on the title — let the title's own onClick run (preview/navigate).
    if (isTitleClick) return;

    // Plain click on the checkbox — let Radix handle the toggle; just record
    // the anchor so the next shift-click has a starting point.
    if (isCheckboxClick) {
      deps.setAnchorIndex(unifiedIndex);
      return;
    }

    // Plain click anywhere else on the row — toggle this row's selection
    // (multi-select by default; clicking another row keeps existing selection).
    event.preventDefault();
    event.stopPropagation();
    applyModifierSelection(
      unifiedIndex,
      deps.unifiedItems,
      deps.isItemSelected,
      deps.setItemSelected
    );
    deps.setAnchorIndex(unifiedIndex);
  };
}

function rowSelectionEnabled(deps: RowClickDeps) {
  return deps.showCheckbox && deps.hasStore;
}
function rowClickTarget(event: React.MouseEvent<HTMLDivElement>) {
  // Click originated in a React-portaled descendant (Radix DropdownMenu
  // items, HoverCard contents, dialog overlays, etc.). React still bubbles
  // synthetic events through the React tree to this onClickCapture handler,
  // but the portaled UI owns the click — swallowing it here would cancel
  // the dropdown item's own onClick (which is exactly the row-action
  // dropdown's symptoms: every item silently does nothing). Bail out so the
  // portal's handler runs normally.
  if (event.target instanceof Node && !event.currentTarget.contains(event.target)) {
    return null;
  }

  const target = event.target as HTMLElement;
  // Radix Checkbox renders as a <button>. Detect checkbox clicks specifically
  // so shift/ctrl+click range selection works on checkboxes too.
  const isCheckboxClick = !!target.closest('[role="checkbox"]');
  // Title text (filename/folder name) is marked with data-row-title. Plain
  // clicks defer to the title's own onClick (preview/navigate); modifier
  // clicks are still treated as selection actions on the row.
  const isTitleClick = !!target.closest("[data-row-title]");
  // The version badge renders as a <div> (not a <button>), so it needs its
  // own opt-out marker; without this, clicking the badge falls through to
  // row selection and never reaches the badge's onClick (WHA-3225).
  const isVersionTrigger = !!target.closest("[data-version-trigger]");
  // Other buttons (action menu, eye preview, version badge, etc.) own their
  // own click entirely — modifiers shouldn't repurpose them as selection
  // actions.
  const isOtherButton =
    !isCheckboxClick && !isTitleClick && (!!target.closest("button") || isVersionTrigger);

  if (isOtherButton) return null;

  return { isCheckboxClick, isTitleClick };
}
